import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import type { AxiosRequestConfig } from 'axios';
import { UserProfile } from '@df-gate-share/shared';
import { setupInterceptors, setupJwtMonitoring, apiClient } from '../lib/api';

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserProfile | null;
  token: string | null;
  requirePasswordChange: boolean;
  login: (token: string, user: UserProfile) => void;
  logout: () => Promise<void>;
  updateToken: (newToken: string) => void;
  clearRequirePasswordChange: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [requirePasswordChange, setRequirePasswordChange] = useState<boolean>(false);
  const [lastActivity, setLastActivity] = useState<number>(Date.now());
  const tokenRef = useRef<string | null>(null);

  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

  const updateToken = useCallback((newToken: string) => {
    setToken(newToken);
    tokenRef.current = newToken;
  }, []);

  // ローカルの認証状態のみをクリア（サーバー通知なし）。
  // リフレッシュ失敗・401など、トークンが既に無効な場面で使用する。
  const clearAuthState = useCallback(() => {
    setToken(null);
    setUser(null);
    setRequirePasswordChange(false);
    tokenRef.current = null;

    // localStorageをクリア
    localStorage.removeItem('user');
    localStorage.removeItem('lastActivity');
  }, []);

  // ログアウト: サーバーにJWTブラックリスト化・リフレッシュトークン削除（Cookieクリア）を
  // 依頼してから、ローカル状態をクリアする。
  // API呼び出しはベストエフォート（失敗してもローカルは必ずクリアする）。
  const logout = useCallback(async () => {
    try {
      // 認証済み（トークン保持）のときのみサーバーへ通知する
      if (tokenRef.current) {
        // _retry: true でレスポンスインターセプターのリフレッシュ再試行を抑止し、
        // ログアウト要求が401時に無限ループ／不要なトークン再発行を起こさないようにする
        await apiClient.post(
          '/api/v1/auth/logout',
          null,
          { _retry: true } as AxiosRequestConfig & { _retry?: boolean }
        );
      }
    } catch {
      // ログアウトAPIの失敗は無視する（ローカル状態は必ずクリアする）
    } finally {
      clearAuthState();
    }
  }, [clearAuthState]);

  const clearRequirePasswordChange = useCallback(() => {
    setRequirePasswordChange(false);
    if (user) {
      const updatedUser = { ...user, requirePasswordChange: false };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  }, [user]);

  const login = useCallback((newToken: string, newUser: UserProfile) => {
    const now = Date.now();
    updateToken(newToken);
    setUser(newUser);
    setRequirePasswordChange(!!newUser.requirePasswordChange);
    setLastActivity(now);

    // localStorageにはユーザー情報のみ保存（トークンは保存しない）
    localStorage.setItem('user', JSON.stringify(newUser));
    localStorage.setItem('lastActivity', now.toString());
  }, [updateToken]);

  // ページリロード時にユーザー情報を復元し、httpOnly Cookie経由でJWTをリフレッシュ
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedLastActivity = localStorage.getItem('lastActivity');

    if (storedUser) {
      const lastActivityTime = storedLastActivity ? parseInt(storedLastActivity, 10) : Date.now();
      const now = Date.now();

      // Check if session has expired
      if (now - lastActivityTime > SESSION_TIMEOUT) {
        // Session expired - clear everything
        localStorage.removeItem('user');
        localStorage.removeItem('lastActivity');
      } else {
        // Session still valid - restore user and refresh JWT via httpOnly cookie
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setRequirePasswordChange(!!parsedUser.requirePasswordChange);
        setLastActivity(lastActivityTime);

        // Refresh JWT using httpOnly cookie (no body needed)
        apiClient.post('/api/v1/auth/refresh')
          .then((res) => {
            updateToken(res.data.jwt);
          })
          .catch(() => {
            // Refresh failed - session invalid. トークンは既に無効なのでローカルのみクリア
            clearAuthState();
          });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAuthenticated = !!token && !!user;

  // Track user activity and check for session timeout
  useEffect(() => {
    if (!isAuthenticated) return;

    const updateActivity = () => {
      const now = Date.now();
      setLastActivity(now);
      localStorage.setItem('lastActivity', now.toString());
    };

    // Check for timeout periodically
    const checkTimeout = () => {
      const storedLastActivity = localStorage.getItem('lastActivity');
      if (storedLastActivity) {
        const lastActivityTime = parseInt(storedLastActivity, 10);
        const now = Date.now();

        if (now - lastActivityTime > SESSION_TIMEOUT) {
          logout();
        }
      }
    };

    // Track user activity events
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((event) => {
      window.addEventListener(event, updateActivity);
    });

    // Check timeout every minute
    const timeoutCheckInterval = setInterval(checkTimeout, 60000);

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(timeoutCheckInterval);
    };
  }, [isAuthenticated, SESSION_TIMEOUT]);

  // APIインターセプターをセットアップ（初回のみ）
  useEffect(() => {
    setupInterceptors(
      () => tokenRef.current,
      updateToken,
      // リフレッシュ失敗時（401）のログアウトはトークンが既に無効なのでローカルのみクリア
      clearAuthState
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 空の依存配列で初回のみ実行

  // Bug-020: JWT有効期限監視。アクセストークン(JWT)はTTL15分のため、
  // 操作はしているが通信のないアイドル状態でも失効してしまう。失効後の操作は
  // API Gateway Authorizer が 403 を返し、ユーザーが操作不能になる事象があった。
  // 認証中は1分ごとに残り有効期限を確認し、残り5分でhttpOnly Cookie経由のリフレッシュを
  // 行うことで、失効前にJWTを更新し 403 を未然に防ぐ。
  useEffect(() => {
    if (!isAuthenticated) return;

    const stopMonitoring = setupJwtMonitoring(
      () => tokenRef.current,
      async () => {
        const res = await apiClient.post('/api/v1/auth/refresh');
        updateToken(res.data.jwt);
      }
    );

    return stopMonitoring;
  }, [isAuthenticated, updateToken]);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    token,
    requirePasswordChange,
    login,
    logout,
    updateToken,
    clearRequirePasswordChange
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
